import { boardLinkShapeProps } from './arrowTerminalLabels.js';

const UNCONNECTABLE_TYPES = new Set(['arrow', 'group']);

function newId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function pageCenter(editor, shape) {
  const bounds =
    editor.getShapePageBounds?.(shape) || editor.getShapePageBounds?.(shape.id);
  if (bounds && Number.isFinite(bounds.w) && Number.isFinite(bounds.h)) {
    return { x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 };
  }
  const w = Number(shape.props?.w) || 0;
  const h = Number(shape.props?.h) || 0;
  return { x: (shape.x || 0) + w / 2, y: (shape.y || 0) + h / 2 };
}

function canReceiveArrowBinding(editor, shape) {
  if (typeof editor?.canBindShapes !== 'function') return true;
  return Boolean(
    editor.canBindShapes({
      fromShape: { type: 'arrow' },
      toShape: shape,
      binding: 'arrow',
    })
  );
}

/**
 * True when a connecting line can bind an endpoint to this shape.
 * Pins, notes, frames, text, images, and other box shapes qualify;
 * arrows, groups, and locked shapes do not.
 */
export function isConnectableShape(editor, shape) {
  if (!shape || UNCONNECTABLE_TYPES.has(shape.type)) return false;
  if (shape.isLocked) return false;
  if (editor?.isShapeOrAncestorLocked?.(shape)) return false;
  return canReceiveArrowBinding(editor, shape);
}

/**
 * The two selected connectable shapes in selection order, or null.
 * Index 0 is the first selected (arrow tail); index 1 is the second (arrowhead).
 */
export function getConnectablePair(editor) {
  const ids = editor?.getSelectedShapeIds?.() || [];
  if (ids.length !== 2) return null;
  const a = editor.getShape?.(ids[0]);
  const b = editor.getShape?.(ids[1]);
  if (!isConnectableShape(editor, a) || !isConnectableShape(editor, b)) return null;
  return [a, b];
}

function bindArrowEnds(editor, arrowId, fromShapeId, toShapeId) {
  const start = {
    type: 'arrow',
    fromId: arrowId,
    toId: fromShapeId,
    props: {
      terminal: 'start',
      isExact: false,
      normalizedAnchor: { x: 0.5, y: 0.5 },
      isPrecise: false,
    },
  };
  const end = {
    type: 'arrow',
    fromId: arrowId,
    toId: toShapeId,
    props: {
      terminal: 'end',
      isExact: false,
      normalizedAnchor: { x: 0.5, y: 0.5 },
      isPrecise: false,
    },
  };

  if (typeof editor.createBindings === 'function') {
    editor.createBindings([
      { id: `binding:${newId('start')}`, ...start },
      { id: `binding:${newId('end')}`, ...end },
    ]);
    return;
  }

  editor.createBinding?.({ id: `binding:${newId('start')}`, ...start });
  editor.createBinding?.({ id: `binding:${newId('end')}`, ...end });
}

/**
 * Create a one-way board arrow from `fromShape` (tail) to `toShape` (head).
 * @returns {string|null} new arrow shape id
 */
export function connectShapesWithLine(editor, fromShape, toShape) {
  if (!editor || !fromShape || !toShape || fromShape.id === toShape.id) return null;
  if (!isConnectableShape(editor, fromShape) || !isConnectableShape(editor, toShape)) {
    return null;
  }

  const from = pageCenter(editor, fromShape);
  const to = pageCenter(editor, toShape);
  const arrowId = `shape:${newId('link')}`;

  const create = () => {
    editor.markHistoryStoppingPoint?.('before-connect-selection');
    const record = {
      id: arrowId,
      type: 'arrow',
      x: from.x,
      y: from.y,
      props: boardLinkShapeProps({
        start: { x: 0, y: 0 },
        end: { x: to.x - from.x, y: to.y - from.y },
        color: 'black',
        size: 'm',
      }),
    };
    if (typeof editor.createShape === 'function') {
      editor.createShape(record);
    } else {
      editor.createShapes?.([record]);
    }
    bindArrowEnds(editor, arrowId, fromShape.id, toShape.id);
    editor.select?.(arrowId);
  };

  if (typeof editor.run === 'function') {
    editor.run(create);
  } else {
    create();
  }

  return arrowId;
}

/**
 * Connect the current two-shape selection: first selected → second selected.
 */
export function connectSelectedShapes(editor) {
  const pair = getConnectablePair(editor);
  if (!pair) return null;
  return connectShapesWithLine(editor, pair[0], pair[1]);
}
